import type {
  FilterParams,
  SearchParams,
  SortParams,
} from '@/modules/common/context/QueryParams/models';
import type {LinkProps, Path} from 'react-router';

import {identity, pick} from 'lodash';
import {useCallback, useMemo} from 'react';

import {SPLIT_OVERRIDE_URL_PARAM} from '@/consts';
import {FILTER_PARAMS_HOTELS} from '@/modules/common/context/QueryParams/models';
import {stringifyQueryParams} from '@/modules/common/context/QueryParams/stringifyQueryParams';
import {useCurrentPage} from '@/modules/common/hooks/useCurrentPage';
import {useDestination} from '@/modules/common/hooks/useDestination';
import {useFilterParams} from '@/modules/common/hooks/useFilterParams';
import {useSearchParams} from '@/modules/common/hooks/useSearchParams';
import {useSortParams} from '@/modules/common/hooks/useSortParams';

type Link = {
  to: Partial<Path>;
  options: {
    replace?: boolean;
    state?: LinkProps['state'];
  };
};

type BuildHotelListLinkParams = {
  getSearchParams?: (previousSearchParams: SearchParams) => SearchParams;
  getFilterParams?: (previousFilterParams: FilterParams) => FilterParams | null;
  getSortParams?: (previousSortParams: SortParams) => SortParams | null;
  additionalState?: LinkProps['state'];
  keepAdditionalParams?: boolean;
};

type BuildOfferListLinkParams = {
  hotelId: string;
  getSearchParams?: (previousSearchParams: SearchParams) => SearchParams;
  getFilterParams?: (previousFilterParams: FilterParams) => FilterParams | null;
  getSortParams?: (previousSortParams: SortParams) => SortParams | null;
  additionalState?: LinkProps['state'];
  keepAdditionalParams?: boolean;
};

type UseLinkBuilderResult = {
  buildHotelListLink: (params: BuildHotelListLinkParams) => Link;
  buildOfferListLink: (params: BuildOfferListLinkParams) => Link;
};

// https://invia.atlassian.net/browse/FFP-4099
const PARAMS_TO_KEEP = [
  'gclid',
  'gbraid',
  'wbraid',
  'msclkid',
  'fbclid',
  'gad_source',
  'utm_*',
  // we dont' want to loose the split we're in
  SPLIT_OVERRIDE_URL_PARAM,
  // kill switch for the honeepot sdk ("true" or "false")
  // set by honeepot on the urls they generate (user comes from honeepot and shouldn't see the widget again)
  'showOdysseus',
];

const getParamsToKeep = () => {
  const trackingParams = [];
  const searchParams = new URLSearchParams(location.search);

  for (const [name, value] of searchParams) {
    if (
      PARAMS_TO_KEEP.some((trackingParam) =>
        trackingParam.endsWith('*') // support wildcards
          ? name.startsWith(trackingParam.slice(0, -1))
          : name === trackingParam
      )
    ) {
      trackingParams.push([name, value]);
    }
  }

  return trackingParams;
};

export const useLinkBuilder = (): UseLinkBuilderResult => {
  const [allSearchParams] = useSearchParams();
  const [currentFilterParams] = useFilterParams();
  const [currentSortParams] = useSortParams();
  const currentPage = useCurrentPage();
  const destination = useDestination();

  // @FIXME there has to be a better way to handle this...
  const currentSearchParams = useMemo(() => {
    if (destination.type !== 'hotel') {
      return allSearchParams;
    }
    return {
      ...allSearchParams,
      destinationId: destination.parentKeyword?.id ?? '',
    };
  }, [destination.type, destination.parentKeyword?.id, allSearchParams]);

  const buildHotelListLink = useCallback(
    ({
      getSearchParams = identity,
      getFilterParams = identity,
      getSortParams = identity,
      additionalState,
      keepAdditionalParams = true,
    }: BuildHotelListLinkParams = {}) => {
      const searchParams = getSearchParams(currentSearchParams);
      const sortParams = getSortParams(currentSortParams);
      const filterParams = getFilterParams(currentFilterParams);
      const hotelFilterParams = filterParams
        ? (pick(filterParams, FILTER_PARAMS_HOTELS) as FilterParams)
        : null;
      const additionalParamsToKeep = getParamsToKeep();

      return {
        to: {
          pathname: `/hotels`,
          search:
            '?' +
            stringifyQueryParams({
              ...searchParams,
              ...hotelFilterParams,
              ...sortParams,
              ...Object.fromEntries(
                keepAdditionalParams ? additionalParamsToKeep : []
              ),
            }),
        },
        options: {
          state: {
            referrer: currentPage,
            ...additionalState,
          },
        },
      };
    },
    [currentPage, currentFilterParams, currentSearchParams, currentSortParams]
  );

  const buildOfferListLink = useCallback(
    ({
      hotelId,
      getSearchParams = identity,
      getFilterParams = identity,
      getSortParams = identity,
      additionalState,
      keepAdditionalParams = true,
    }: BuildOfferListLinkParams) => {
      const searchParams = getSearchParams(currentSearchParams);
      const filterParams = getFilterParams(currentFilterParams);
      const sortParams = getSortParams(currentSortParams);
      const additionalParamsToKeep = getParamsToKeep();

      return {
        to: {
          pathname: `/hotel/${hotelId}/offers`,
          search:
            '?' +
            stringifyQueryParams({
              ...searchParams,
              ...filterParams,
              ...sortParams,
              ...Object.fromEntries(
                keepAdditionalParams ? additionalParamsToKeep : []
              ),
            }),
        },
        options: {
          state: {
            referrer: currentPage,
            ...additionalState,
          },
        },
      };
    },
    [currentPage, currentFilterParams, currentSearchParams, currentSortParams]
  );

  return {
    buildHotelListLink,
    buildOfferListLink,
  };
};
